import { Component } from 'react';
import { GeneratorLayout, genStyles } from '../converter-layout/generator-layout';

type LibState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'ready'; lib: any }
    | { kind: 'error'; message: string };

interface State {
    password: string;
    salt: string;
    costN: number;
    blockSizeR: number;
    parallelismP: number;
    keyLen: number;
    output: string;
    error: string | null;
    lib: LibState;
    busy: boolean;
    copied: boolean;
}

function bytesToHex(bytes: ArrayLike<number>): string {
    return Array.from(bytes as any).map((b: any) => b.toString(16).padStart(2, '0')).join('');
}

export default class ScryptGeneratorComponent extends Component<{ feature?: string }, State> {
    private copyTimeout: ReturnType<typeof setTimeout> | null = null;
    state: State = {
        password: 'p4ssw0rd!',
        salt: '',
        costN: 17,
        blockSizeR: 8,
        parallelismP: 1,
        keyLen: 64,
        output: '',
        error: null,
        lib: { kind: 'idle' },
        busy: false,
        copied: false,
    };

    componentDidMount() {
        const salt = new Uint8Array(16);
        crypto.getRandomValues(salt);
        this.setState({ salt: bytesToHex(salt) });
    }

    componentWillUnmount() {
        if (this.copyTimeout) clearTimeout(this.copyTimeout);
    }

    private async ensureLib(): Promise<any | null> {
        if (this.state.lib.kind === 'ready') return this.state.lib.lib;
        this.setState({ lib: { kind: 'loading' } });
        try {
            const lib = await import('hash-wasm');
            this.setState({ lib: { kind: 'ready', lib } });
            return lib;
        } catch (e: any) {
            this.setState({ lib: { kind: 'error', message: e.message || 'Failed to load WASM' } });
            return null;
        }
    }

    generate = async () => {
        this.setState({ busy: true });
        const lib = await this.ensureLib();
        if (!lib) { this.setState({ busy: false }); return; }
        try {
            const saltBytes = this.state.salt.match(/.{1,2}/g)?.map(b => parseInt(b, 16)) || [];
            const N = 1 << this.state.costN;
            const hex = await lib.scrypt({
                password: this.state.password,
                salt: new Uint8Array(saltBytes),
                costFactor: N,
                blockSize: this.state.blockSizeR,
                parallelism: this.state.parallelismP,
                hashLength: this.state.keyLen,
                outputType: 'hex',
            });
            this.setState({ output: hex, error: null, busy: false, copied: false });
        } catch (e: any) {
            this.setState({ error: e.message || 'scrypt failed', output: '', busy: false });
        }
    };

    copyOutput = () => {
        if (!this.state.output) return;
        navigator.clipboard.writeText(this.state.output).then(() => {
            if (this.copyTimeout) clearTimeout(this.copyTimeout);
            this.setState({ copied: true });
            this.copyTimeout = setTimeout(() => this.setState({ copied: false }), 2000);
        });
    };

    render() {
        const { password, salt, costN, blockSizeR, parallelismP, keyLen, output, error, lib, busy, copied } = this.state;

        const form = (
            <>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Input</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="sc-pw">Password</label>
                        <input id="sc-pw" type="text" value={password} spellCheck={false} autoComplete="off"
                            onChange={e => this.setState({ password: e.target.value })} />
                    </div>
                    <div className={genStyles.field}>
                        <label htmlFor="sc-salt">Salt (hex)</label>
                        <input id="sc-salt" type="text" value={salt} spellCheck={false}
                            onChange={e => this.setState({ salt: e.target.value.replace(/[^0-9a-fA-F]/g, '') })} />
                    </div>
                </div>
                <div className={genStyles.section}>
                    <div className={genStyles.sectionHeader}><span className={genStyles.sectionTitle}>Parameters</span></div>
                    <div className={genStyles.field}>
                        <label htmlFor="sc-n">log₂(N) — cost</label>
                        <input id="sc-n" type="number" inputMode="decimal" min={10} max={20} value={costN}
                            onChange={e => this.setState({ costN: Math.max(10, Math.min(20, +e.target.value || 17)) })} />
                        <span style={{ fontSize: '0.7rem', color: '#94a3b8' }}>RFC 7914 recommends ≥ 14</span>
                    </div>
                    <div className={genStyles.fieldRow}>
                        <div className={genStyles.field}>
                            <label htmlFor="sc-r">r (block size)</label>
                            <input id="sc-r" type="number" inputMode="decimal" min={1} max={32} value={blockSizeR}
                                onChange={e => this.setState({ blockSizeR: +e.target.value || 8 })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="sc-p">p (parallelism)</label>
                            <input id="sc-p" type="number" inputMode="decimal" min={1} max={8} value={parallelismP}
                                onChange={e => this.setState({ parallelismP: +e.target.value || 1 })} />
                        </div>
                        <div className={genStyles.field}>
                            <label htmlFor="sc-len">Key length (bytes)</label>
                            <input id="sc-len" type="number" inputMode="decimal" min={16} max={128} value={keyLen}
                                onChange={e => this.setState({ keyLen: +e.target.value || 64 })} />
                        </div>
                    </div>
                    <button className={`${genStyles.iconBtn} ${genStyles.iconBtnPrimary}`} onClick={this.generate}
                        disabled={busy || lib.kind === 'loading'} style={{ alignSelf: 'flex-start' }}>
                        {busy ? 'Hashing…' : lib.kind === 'loading' ? 'Loading WASM…' : '⚙ Hash'}
                    </button>
                </div>
            </>
        );

        return (
            <GeneratorLayout
                form={form}
                previewLabel="Scrypt hash (hex)"
                previewMeta={output ? `${output.length} chars` : undefined}
                preview={<pre className={genStyles.previewArea} aria-label="Scrypt output">{output || 'Hash will appear here once you click Hash.'}</pre>}
                previewActions={
                    <button className={`${genStyles.iconBtn} ${copied ? genStyles.iconBtnSuccess : ''}`}
                        onClick={this.copyOutput} disabled={!output}>
                        {copied ? '✓ Copied' : '📋 Copy hash'}
                    </button>
                }
                error={error || (lib.kind === 'error' ? (lib as any).message : null)}
            />
        );
    }
}
